import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { toast } from '@/ui/primitives/aviso';

import { EXECUCOES_RECENTES, useDispararBusca, useExecucoesCaptura } from './api';
import { avisarBusca } from './avisos';
import { mensagemErroCaptura } from './textos';

/** Depois disto o acompanhamento desiste e avisa que o resultado sai no histórico. */
export const ACOMPANHAR_ATE_MS = 3 * 60_000;

/**
 * "Buscar agora": dispara (202) e acompanha a execução pela primeira página do histórico (a mesma
 * consulta da tabela) até ela terminar, para o toast dizer o que veio. Desmontar cancela tudo
 * (timer e consulta em andamento). Falha passageira ao acompanhar não vira erro: a busca já foi
 * disparada e o servidor segue com ela.
 */
export function useBuscarAgora(empresaId: string, fuso: string) {
  const cliente = useQueryClient();
  const disparar = useDispararBusca(empresaId);
  const execucaoId = disparar.data?.execucaoId ?? null;
  const [desistiuDe, setDesistiuDe] = useState<string | null>(null);
  const avisadas = useRef(new Set<string>());

  const recentes = useExecucoesCaptura(empresaId, EXECUCOES_RECENTES, {
    habilitado: execucaoId !== null,
    acompanhar: execucaoId !== null && desistiuDe !== execucaoId,
  });
  const terminada = recentes.data?.items.find(
    (e) => e.id === execucaoId && e.status !== 'EXECUTANDO',
  );
  const acompanhando = execucaoId !== null && !terminada && desistiuDe !== execucaoId;

  useEffect(() => {
    if (!terminada || avisadas.current.has(terminada.id)) return;
    avisadas.current.add(terminada.id);
    avisarBusca(terminada);
    void cliente.invalidateQueries({ queryKey: ['captura'] });
  }, [terminada, cliente]);

  useEffect(() => {
    if (!acompanhando) return undefined;
    const timer = setTimeout(() => {
      avisarBusca(null);
      setDesistiuDe(execucaoId);
    }, ACOMPANHAR_ATE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [acompanhando, execucaoId]);

  return {
    buscando: disparar.isPending || acompanhando,
    buscar: () => {
      disparar.mutate(undefined, {
        onError: (erro) => toast.error(mensagemErroCaptura(erro, fuso)),
      });
    },
  };
}
